import { bucket } from "../../firebase/config.js"
import firebase from '../../firebase/compat'
import { db, timestamp } from "../../firebase/config";
import { useContext, useState } from "react";
import { v4 as uuid } from 'uuid'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import { useFirestore, useFirestoreOrderBy } from "../../firebase/useFirestore.js";
import usePermissions from "../../hooks/usePermissions";
import { client } from "../../hooks/Client";
import Table from "../common/table/Table.jsx";
import TableCell from "../common/table/TableCell.jsx";
import Tooltip from "../common/Tooltip.jsx";
import ToggleButton from "../common/ToggleButton.jsx";
import AdresFields from "./AdresFields.jsx";
import SectorSelector from "./SectorSelector.jsx";
import HuisstijlSettings from "./HuisstijlSettings.jsx";
import MfaPolicySetting from "./MfaPolicySetting.jsx";

/**
 * @param {object} props
 * @param {string} props.selectedOption - the active Settings tab id ('1' = Algemeen)
 */
const GeneralSettings = ({selectedOption}) => {
      // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)
    const { has, ready } = usePermissions()
    const canEdit = !ready || has('settings.edit')

    // Firestore
    const compagny = useFirestore("CompagnyMeta")
    const compagnyContext = useFirestoreOrderBy("CompagnyContext", "Position", "asc")
    const compagnyCosts = useFirestoreOrderBy("CompagnyCosts", "Year", "desc")

    // State — document upload
    const [showUpload, setShowUpload] = useState(false)
    const [selectedFile, setSelectedFile] = useState(/** @type {File | null} */ (null))
    const [docType, setDocType] = useState('algemeen')

    // State — add cost year
    const [showAddCost, setShowAddCost] = useState(false)
    const [newYear, setNewYear] = useState('')

    // The Firestore doc is created BEFORE the file is uploaded, so the
    // embedCompagnyDocument onFinalize trigger can always find it by FilePath.
    const uploadDocumentHandler = () => {
        if (!selectedFile) return

        const id = uuid()
        const filePath = `CompagnyContext/${client}/${id}-${selectedFile.name}`

        db.collection("CompagnyContext")
        .add({
            ID: id,
            CompagnyID: client,
            Compagny: client,
            Position: compagnyContext ? compagnyContext.length + 1 : 1,
            Timestamp: timestamp,
            Title: selectedFile.name,
            DocType: docType,
            URL: '',
            FilePath: filePath,
        })
        .then((ref) => {
            const uploadTask = bucket.ref(filePath).put(selectedFile)
            uploadTask.on('state_changed', null,
                (err) => alert(err),
                () => {
                    uploadTask.snapshot.ref.getDownloadURL().then((url) => {
                        ref.update({ URL: url }).then(() => {
                            setSaved('flex')
                            setShowUpload(false)
                            setSelectedFile(null)
                            setDocType('algemeen')
                        })
                    })
                }
            )
        })
    }

    // Soft delete (ADR-008). Het Storage-bestand en de embedding blijven
    // bewust staan: het document is dan te herstellen door de Deleted-vlag
    // terug te zetten, en de uuid-genamespacede FilePath kan toch nooit
    // opnieuw geüpload worden. Toekomstige lezers van CompagnyContext die
    // de centrale useFirestore-filtering omzeilen (bv. een Cloud Function
    // met vector-search) moeten Deleted-docs zelf uitsluiten.
    const deleteDocumentHandler = (e) => {
        const docid = e.currentTarget.dataset.docid

        db.collection("CompagnyContext")
        .doc(docid)
        .update({
            Deleted: true,
            DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
            DeletedBy: auth?.ID || '',
        })
        .then(() => setSaved('flex'))
    }

    // --- Kosten per jaar ---
    // Elke handler schrijft alléén zijn eigen veld — nooit een tweede,
    // onafhankelijk gezet veld meenemen.
    // AI-CONTEXT: See docs/pitfalls.md#state-handler-overwrites-sibling-field
    const COST_NUMBER_FIELDS = ['TotalCosts', 'TotalIncome', 'SupportVolunteerHours', 'IncidentalCosts']

    const costNumberFieldHandler = (e) => {
        const docid = e.target.dataset.docid
        const field = e.target.dataset.field
        if (!COST_NUMBER_FIELDS.includes(field)) return

        const raw = e.target.value
        const value = raw === '' ? null : Number(raw)

        db.collection("CompagnyCosts")
        .doc(docid)
        .update({ [field]: value })
        .then(() => setSaved('flex'))
    }

    const incidentalNoteHandler = (e) => {
        const docid = e.target.dataset.docid

        db.collection("CompagnyCosts")
        .doc(docid)
        .update({ IncidentalNote: e.target.value })
        .then(() => setSaved('flex'))
    }

    // Dekking is een percentage — buiten 0–100 schrijven we niet.
    const coveragePctHandler = (e) => {
        const docid = e.target.dataset.docid
        const raw = e.target.value
        const value = raw === '' ? null : Number(raw)
        if (value !== null && (!Number.isFinite(value) || value < 0 || value > 100)) return

        db.collection("CompagnyCosts")
        .doc(docid)
        .update({ CoveragePct: value })
        .then(() => setSaved('flex'))
    }

    // Soft delete (ADR-008) — de centrale useFirestore-filtering verbergt de rij.
    const deleteCostHandler = (e) => {
        const docid = e.currentTarget.dataset.docid

        db.collection("CompagnyCosts")
        .doc(docid)
        .update({
            Deleted: true,
            DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
            DeletedBy: auth?.ID || '',
        })
        .then(() => setSaved('flex'))
    }

    const addCostYearHandler = () => {
        const year = Number(newYear)
        if (!Number.isFinite(year) || year < 2000) return

        const exists = (compagnyCosts || []).some((c) => Number(c.Year) === year)
        if (exists) {
            alert('Er bestaat al een rij voor dit jaar.')
            return
        }

        db.collection("CompagnyCosts")
        .add({
            ID: uuid(),
            CompagnyID: client,
            Compagny: client,
            Timestamp: timestamp,
            Year: year,
            TotalCosts: 0,
            TotalIncome: null,
            SupportVolunteerHours: null,
            IncidentalCosts: null,
            IncidentalNote: '',
            CoveragePct: null,
            Confirmed: false,
            SourceDocID: '',
            SourceTitle: '',
            ExtractedCandidate: null,
        })
        .then(() => {
            setSaved('flex')
            setShowAddCost(false)
            setNewYear('')
        })
    }

    const saveName = (e) => {

        const communityName = e.target.value
        const docid = e.target.dataset.docid

        db.collection("CompagnyMeta")
        .doc(docid)
        .update({
            CommunityName: communityName
        })
        .then(() => {
            setSaved('flex')
         })
    }

    const notificationHandler = (e) => {

        const value = e.target.options[e.target.selectedIndex].value 
        const docid = e.target.dataset.docid

        db.collection("CompagnyMeta")
        .doc(docid)
        .update({
            UpdateEmail: value
        })
        .then(() => {
            setSaved('flex')
         })
    }

    // Website
    const WebsiteHandler = (e) => {

        const value = e.target.value
        const docid = e.target.dataset.docid

        db.collection("CompagnyMeta")
        .doc(docid)
        .update({
            Website: value
        })
        .then(() => {
            setSaved('flex')
         })
    }

    // Email
    const EmailHandler = (e) => {

        const value = e.target.value
        const docid = e.target.dataset.docid

        db.collection("CompagnyMeta")
        .doc(docid)
        .update({
            Email: value
        })
        .then(() => {
            setSaved('flex')
         })
    }

  return (
    <>
    {selectedOption === '1' &&
        <div className="settings-container">
            {compagny && compagny.map(comp => (
            <div key={comp.ID}>
                {/* Eerst: de sectorkeuze bepaalt welke sectorspecifieke contextvragen de
                    Theory of Change aanbiedt, dus die zet je vóór de rest.
                    AI-CONTEXT: See docs/features/sector-context-questions.md */}
                <SectorSelector comp={comp} canEdit={canEdit} />
                <div className="dashboard-container">
                    <h2>Bedrijfsnaam</h2>
                    <input className="input-classic" type="text" data-docid={comp.docid} defaultValue={comp.CommunityName} onChange={saveName} disabled={!canEdit} />
                </div>
                <div className="dashboard-container logo-container">
                    <h2>E-mailadres</h2>
                    <input className="input-classic" type="text" data-docid={comp.docid} defaultValue={comp.Email} onChange={EmailHandler} disabled={!canEdit} />
                </div>
                <div className="dashboard-container logo-container">
                    <h2>Website</h2>
                    <input className="input-classic" type="text" data-docid={comp.docid} defaultValue={comp.Website} onChange={WebsiteHandler} disabled={!canEdit} />
                </div>
                <AdresFields comp={comp} />
                {/* Gedeeld met de Huisstijl-tab van Impact in beeld — één schrijfpad. */}
                <HuisstijlSettings comp={comp} canEdit={canEdit} />
                <div className="dashboard-container logo-container">
                    <h2>Modules</h2>
                    <div className="settings-general-module-settings-item-container">
                        <p>Impact meten</p>
                        <ToggleButton docid={comp.docid} id={'ImpactFunctionality'} collection={"CompagnyMeta"} value={comp.ImpactFunctionality} />
                    </div>
                    <div className="settings-general-module-settings-item-container">
                        <p>Medewerkers</p>
                        <ToggleButton docid={comp.docid} id={'Personas'} collection={"CompagnyMeta"} value={comp.Personas} />
                    </div>
                    <div className="settings-general-module-settings-item-container">
                        <p>Impact in beeld</p>
                        <ToggleButton docid={comp.docid} id={'ImpactPerEuro'} collection={"CompagnyMeta"} value={comp.ImpactPerEuro} />
                    </div>
                    <div className="settings-general-module-settings-item-container">
                        <p>Huishoudens</p>
                        <ToggleButton docid={comp.docid} id={'HouseVisits'} collection={"CompagnyMeta"} value={comp.HouseVisits} />
                    </div>
                </div>
            </div>
            ))}
            <div className="profile-inner-container">
                <div className="divider">
                    <h2>Documenten</h2>
                    <p className="settings-section-explainer">Upload documenten van je organisatie (zoals een jaarverslag). De AI gebruikt deze als context.</p>
                    <Table
                        columns={[
                            { key: 'title', label: 'DOCUMENT' },
                            { key: 'type', label: 'TYPE', searchable: false },
                            { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                        ]}
                        items={compagnyContext || []}
                        getItemKey={(doc) => doc.ID}
                        renderRow={(doc) => (
                            <>
                                <TableCell>
                                    {doc.URL
                                        ? <a href={doc.URL} target="_blank" rel="noreferrer">{doc.Title}</a>
                                        : <span>{doc.Title}</span>}
                                </TableCell>
                                <TableCell>
                                    <p>{doc.DocType || 'algemeen'}</p>
                                </TableCell>
                                <TableCell>
                                    {canEdit &&
                                        <Tooltip content={'Document verwijderen'} top='-60px' width={'30px'}>
                                            <DeleteOutlinedIcon className='table-delete-icon' data-docid={doc.docid} onClick={deleteDocumentHandler} />
                                        </Tooltip>}
                                </TableCell>
                            </>
                        )}
                        searchAccessor={(doc) => doc?.Title || ''}
                        searchPlaceholder='Zoeken op documentnaam'
                        emptyMessage='Nog geen documenten geüpload.'
                        addRow={canEdit ? {
                            toolTipContent: 'Document toevoegen',
                            onClick: () => setShowUpload(!showUpload),
                        } : null}
                    />
                    {showUpload &&
                        <div className='add-options-container'>
                            <select className="input-classic" value={docType} onChange={(e) => setDocType(e.target.value)}>
                                <option value="algemeen">Algemeen</option>
                                <option value="jaarverslag">Jaarverslag</option>
                                <option value="beleidsplan">Beleidsplan</option>
                            </select>
                            <input type="file" onChange={(e) => setSelectedFile(e.target.files[0] || null)} />
                            <div className='add-options-upload-buttons-container'>
                                <button className='button-simple' onClick={() => { setShowUpload(false); setSelectedFile(null) }}>Annuleren</button>
                                <button className='button-simple' onClick={uploadDocumentHandler} disabled={!selectedFile}>Uploaden</button>
                            </div>
                        </div>}
                </div>
                <div className="divider">
                    <h2>Kosten per jaar</h2>
                    <p className="settings-section-explainer">De financiële gegevens per boekjaar voor het kengetal 'Impact per euro' (methodiek v1.0). Vrijwilligersuren zijn alleen de uren van ondersteunende vrijwilligers (bestuur, klusdienst, gastvrouw, chauffeur) — deelnemers tellen niet mee, hun waarde zit al in de teller. Zet 'Bevestigd' aan zodra de kosten overeenkomen met de vastgestelde jaarrekening.</p>
                    <Table
                        columns={[
                            { key: 'year', label: 'JAAR' },
                            { key: 'costs', label: 'TOTALE KOSTEN', searchable: false },
                            { key: 'income', label: 'TOTALE INKOMSTEN', searchable: false },
                            { key: 'volunteerHours', label: 'VRIJWILLIGERSUREN', searchable: false },
                            { key: 'incidental', label: 'INCIDENTELE POSTEN', searchable: false },
                            { key: 'coverage', label: 'DEKKING %', searchable: false },
                            { key: 'confirmed', label: 'BEVESTIGD', searchable: false },
                            { key: 'source', label: 'BRON', searchable: false },
                            { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                        ]}
                        items={compagnyCosts || []}
                        getItemKey={(c) => c.ID}
                        renderRow={(c) => {
                            const source = (compagnyContext || []).find((d) => d.ID === c.SourceDocID)
                            return (
                                <>
                                    <TableCell>
                                        <p>{c.Year}</p>
                                    </TableCell>
                                    <TableCell>
                                        <input type="number" data-docid={c.docid} data-field="TotalCosts" defaultValue={c.TotalCosts ?? ''} onChange={costNumberFieldHandler} disabled={!canEdit} />
                                    </TableCell>
                                    <TableCell>
                                        <input type="number" data-docid={c.docid} data-field="TotalIncome" defaultValue={c.TotalIncome ?? ''} onChange={costNumberFieldHandler} disabled={!canEdit} />
                                    </TableCell>
                                    <TableCell>
                                        <input type="number" data-docid={c.docid} data-field="SupportVolunteerHours" defaultValue={c.SupportVolunteerHours ?? ''} onChange={costNumberFieldHandler} disabled={!canEdit} />
                                    </TableCell>
                                    <TableCell>
                                        <input type="number" data-docid={c.docid} data-field="IncidentalCosts" defaultValue={c.IncidentalCosts ?? ''} onChange={costNumberFieldHandler} disabled={!canEdit} />
                                        <input type="text" placeholder="Toelichting" data-docid={c.docid} defaultValue={c.IncidentalNote ?? ''} onChange={incidentalNoteHandler} disabled={!canEdit} />
                                    </TableCell>
                                    <TableCell>
                                        <input type="number" min="0" max="100" data-docid={c.docid} defaultValue={c.CoveragePct ?? ''} onChange={coveragePctHandler} disabled={!canEdit} />
                                    </TableCell>
                                    <TableCell>
                                        <ToggleButton docid={c.docid} id='Confirmed' value={c.Confirmed} collection='CompagnyCosts' />
                                    </TableCell>
                                    <TableCell>
                                        {source && source.URL
                                            ? <a href={source.URL} target="_blank" rel="noreferrer">{c.SourceTitle || source.Title}</a>
                                            : <span>{c.SourceTitle || '-'}</span>}
                                    </TableCell>
                                    <TableCell>
                                        {canEdit &&
                                            <Tooltip content={'Rij verwijderen'} top='-60px' width={'30px'}>
                                                <DeleteOutlinedIcon className='table-delete-icon' data-docid={c.docid} onClick={deleteCostHandler} />
                                            </Tooltip>}
                                    </TableCell>
                                </>
                            )
                        }}
                        showSearch={false}
                        emptyMessage='Nog geen kosten ingevoerd.'
                        addRow={canEdit ? {
                            toolTipContent: 'Jaar toevoegen',
                            onClick: () => setShowAddCost(!showAddCost),
                        } : null}
                    />
                    {showAddCost &&
                        <div className='add-options-container'>
                            <input type="number" placeholder="Jaar (bijv. 2025)" value={newYear} onChange={(e) => setNewYear(e.target.value)} />
                            <div className='add-options-upload-buttons-container'>
                                <button className='button-simple' onClick={() => { setShowAddCost(false); setNewYear('') }}>Annuleren</button>
                                <button className='button-simple' onClick={addCostYearHandler} disabled={!newYear}>Toevoegen</button>
                            </div>
                        </div>}
                </div>
            </div>
        </div>
        }
    {/* Tweestapsverificatie verplicht — één keer, niet per CompagnyMeta-document.
        Eigen poort: canEdit hierboven valt open zolang de rechten laden; de
        server weigert sowieso zonder 'settings.edit'.
        AI-CONTEXT: See docs/features/mfa-totp.md (fase 5) */}
    {selectedOption === '1' && <MfaPolicySetting canEdit={ready && has('settings.edit')} />}
    </>
  )
}

export default GeneralSettings